import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2 className="modal-title">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function EntityForm({ entity, onSave, onClose }) {
  const [form, setForm] = useState({
    name: entity?.name || '',
    tms_client_name: entity?.tms_client_name || '',
    cadence: entity?.cadence || 'EOD',
    show_open_loads: entity?.show_open_loads ?? false,
    status: entity?.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!form.name || !form.tms_client_name) { setError('Name and TMS Client Name are required'); return; }
    setSaving(true);
    let result;
    if (entity?.id) {
      result = await supabase.from('entities').update(form).eq('id', entity.id);
    } else {
      result = await supabase.from('entities').insert(form);
    }
    if (result.error) { setError(result.error.message); setSaving(false); return; }
    onSave();
  }

  return (
    <>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <label className="form-label">Customer Name</label>
        <input className="form-input" value={form.name} onChange={e => setForm(p=>({...p, name:e.target.value}))} placeholder="e.g. Hooper Corp" />
      </div>
      <div className="form-group">
        <label className="form-label">TMS Client Name (exact match)</label>
        <input className="form-input font-mono" value={form.tms_client_name} onChange={e => setForm(p=>({...p, tms_client_name:e.target.value}))} placeholder="e.g. HOOPER CORP" />
        <p style={{fontSize:11,color:'var(--text-secondary)',marginTop:4}}>Must match exactly as it appears in your TMS export</p>
      </div>
      <div className="form-group">
        <label className="form-label">Report Cadence</label>
        <select className="form-input form-select" value={form.cadence} onChange={e => setForm(p=>({...p, cadence:e.target.value}))}>
          <option value="Twice Daily">Twice Daily (10am & 4pm)</option>
          <option value="EOD">End of Day</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Status</label>
        <select className="form-input form-select" value={form.status} onChange={e => setForm(p=>({...p, status:e.target.value}))}>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>
      <div className="form-group">
        <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer'}}>
          <input type="checkbox" checked={form.show_open_loads} onChange={e => setForm(p=>({...p, show_open_loads:e.target.checked}))} />
          <span>Show Open (uncovered) loads to customer</span>
        </label>
      </div>
      <div className="modal-actions">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Customer'}
        </button>
      </div>
    </>
  );
}

function ContactForm({ entityId, contact, onSave, onClose }) {
  const [form, setForm] = useState({
    name: contact?.name || '',
    email: contact?.email || '',
    active: contact?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!form.name || !form.email) { setError('Name and email are required'); return; }
    setSaving(true);
    let result;
    if (contact?.id) {
      result = await supabase.from('contacts').update(form).eq('id', contact.id);
    } else {
      result = await supabase.from('contacts').insert({ ...form, entity_id: entityId });
    }
    if (result.error) { setError(result.error.message); setSaving(false); return; }
    onSave();
  }

  return (
    <>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <label className="form-label">Contact Name</label>
        <input className="form-input" value={form.name} onChange={e => setForm(p=>({...p, name:e.target.value}))} placeholder="e.g. Tobin Hooper" />
      </div>
      <div className="form-group">
        <label className="form-label">Email</label>
        <input className="form-input" type="email" value={form.email} onChange={e => setForm(p=>({...p, email:e.target.value}))} placeholder="tobin@hoopercorp.com" />
      </div>
      <div className="form-group">
        <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer'}}>
          <input type="checkbox" checked={form.active} onChange={e => setForm(p=>({...p, active:e.target.checked}))} />
          <span>Active</span>
        </label>
      </div>
      <div className="modal-actions">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Contact'}
        </button>
      </div>
    </>
  );
}

function SubscriberForm({ entityId, subscriber, onSave, onClose }) {
  const [form, setForm] = useState({
    name:     subscriber?.name     || '',
    email:    subscriber?.email    || '',
    cadence:  subscriber?.cadence  || 'daily',
    timezone: subscriber?.timezone || 'America/Chicago',
    active:   subscriber?.active   ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  async function handleSave() {
    if (!form.name || !form.email) { setError('Name and email are required'); return; }
    setSaving(true);

    let result;
    if (subscriber?.id) {
      result = await supabase.from('subscribers').update(form).eq('id', subscriber.id);
    } else {
      result = await supabase.from('subscribers').insert({ ...form, entity_id: entityId });
    }

    if (result.error) { setError(result.error.message); setSaving(false); return; }

    // SEND INVITATION EMAIL ON NEW SUBSCRIBER
    if (!subscriber?.id) {
      // FETCH THE NEW SUBSCRIBER'S ID
      const { data: newSub } = await supabase
      .from('subscribers')
      .select('id')
      .eq('email', form.email)
      .single();

      if (newSub) {
        const resp = await fetch('https://arpjtibkfckuzkxwguaj.supabase.co/functions/v1/invite-subscriber', {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ subscriber_id: newSub.id })
        });
        const data = await resp.json();
        console.log('invite-subscriber response:', data);
      }
    }

    onSave();
  }

  return (
    <>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <label className="form-label">Name</label>
        <input className="form-input" value={form.name}
          onChange={e => setForm(p=>({...p, name:e.target.value}))}
          placeholder="e.g. Jane Smith" />
      </div>
      <div className="form-group">
        <label className="form-label">Email</label>
        <input className="form-input" type="email" value={form.email}
          onChange={e => setForm(p=>({...p, email:e.target.value}))}
          placeholder="jane@hoopercorp.com" />
      </div>
      <div className="form-group">
        <label className="form-label">Timezone</label>
        <select className="form-input form-select" value={form.timezone}
          onChange={e => setForm(p=>({...p, timezone:e.target.value}))}>
          <option value="America/Chicago">Central Time (CT)</option>
          <option value="America/New_York">Eastern Time (ET)</option>
          <option value="America/Denver">Mountain Time (MT)</option>
          <option value="America/Los_Angeles">Pacific Time (PT)</option>
          <option value="America/Phoenix">Arizona (no DST)</option>
          <option value="America/Anchorage">Alaska (AKT)</option>
          <option value="Pacific/Honolulu">Hawaii (HST)</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Cadence</label>
        <select className="form-input form-select" value={form.cadence}
          onChange={e => setForm(p=>({...p, cadence:e.target.value}))}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
      </div>
      <div className="form-group">
        <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer'}}>
          <input type="checkbox" checked={form.active}
            onChange={e => setForm(p=>({...p, active:e.target.checked}))} />
          <span>Active</span>
        </label>
      </div>
      <div className="modal-actions">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Subscriber'}
        </button>
      </div>
    </>
  );
}

const STATUS_BADGE = {
  active:    { cls: 'badge-green',  label: 'Active' },
  draft:     { cls: 'badge-yellow', label: 'Draft' },
  dismissed: { cls: 'badge-gray',   label: 'Dismissed' },
};

export default function CustomersPage() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [tab, setTab] = useState('active');

  async function fetchEntities() {
    setLoading(true);
    const { data } = await supabase
      .from('entities')
      .select('*, contacts(*), subscribers(*)')
      .order('name');
    setEntities(data || []);
    setLoading(false);
  }

  useEffect(() => { fetchEntities(); }, []);

  function closeModal() { setModal(null); fetchEntities(); }

  async function setStatus(entity, status) {
    await supabase.from('entities').update({ status }).eq('id', entity.id);
    fetchEntities();
  }

  const filtered = entities.filter(e => e.status === tab);
  const counts = { active: 0, draft: 0, dismissed: 0 };
  entities.forEach(e => counts[e.status]++);

  if (loading) return <div style={{padding:40,textAlign:'center'}}><span className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-subtitle">Manage entities, contacts, and report preferences</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({ type: 'entity' })}>+ Add Customer</button>
      </div>

      <div className="tabs">
        {['active','draft','dismissed'].map(t => (
          <button key={t} className={`tab${tab===t?' active':''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase()+t.slice(1)} {counts[t] > 0 && <span style={{marginLeft:4,background:'rgba(0,15,159,0.1)',borderRadius:10,padding:'1px 7px',fontSize:11}}>{counts[t]}</span>}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">{tab==='active'?'🏢':tab==='draft'?'📝':'🚫'}</div>
          <h3>No {tab} customers</h3>
          <p>{tab==='active' ? 'Add a customer or activate a draft' : tab==='draft' ? 'New customers appear here after import' : 'Dismissed customers will appear here'}</p>
        </div>
      )}

      {filtered.map(entity => (
        <div className="card" key={entity.id}>
          <div className="card-header">
            <div>
              <span className="card-title">{entity.name}</span>
              <span className={`badge ${STATUS_BADGE[entity.status].cls}`} style={{marginLeft:8}}>
                {STATUS_BADGE[entity.status].label}
              </span>
              <div style={{marginTop:6, display:'flex', gap:8, flexWrap:'wrap'}}>
                <span className="badge badge-blue">{entity.cadence}</span>
                <span className="badge badge-gray" style={{fontFamily:'var(--font-mono)', fontSize:10}}>{entity.tms_client_name}</span>
                {entity.show_open_loads
                  ? <span className="badge badge-yellow">Shows Open Loads</span>
                  : <span className="badge badge-gray">Hides Open Loads</span>}
              </div>
            </div>
            <div className="flex gap-2" style={{flexWrap:'wrap', justifyContent:'flex-end'}}>
              {entity.status !== 'active' && (
                <button className="btn btn-sky btn-sm" onClick={() => setStatus(entity, 'active')}>Activate</button>
              )}
              {entity.status === 'active' && (
                <button className="btn btn-ghost btn-sm"
                  onClick={() => setModal({ type: 'subscriber', entityId: entity.id })}>
                  + Subscriber
                </button>
              )}
              {entity.status !== 'dismissed' && (
                <button className="btn btn-ghost btn-sm" onClick={() => setModal({ type: 'entity', entity })}>Edit</button>
              )}
              {entity.status === 'active' && (
                <button className="btn btn-danger btn-sm" onClick={() => setStatus(entity, 'dismissed')}>Dismiss</button>
              )}
              {entity.status === 'dismissed' && (
                <button className="btn btn-ghost btn-sm" onClick={() => setModal({ type: 'entity', entity })}>View</button>
              )}
            </div>
          </div>

          {entity.status === 'active' && (
            <div style={{marginTop:4}}>
              {(entity.contacts || []).filter(c=>c.active).length === 0 ? (
                <p style={{fontSize:12, color:'var(--text-secondary)'}}>No contacts yet — add one above</p>
              ) : (
                <table style={{width:'100%', fontSize:13}}>
                  <thead>
                    <tr style={{background:'#f8f9fc'}}>
                      <th style={{padding:'7px 12px', textAlign:'left', color:'var(--text-secondary)', fontSize:11, fontWeight:600}}>NAME</th>
                      <th style={{padding:'7px 12px', textAlign:'left', color:'var(--text-secondary)', fontSize:11, fontWeight:600}}>EMAIL</th>
                      <th style={{padding:'7px 12px'}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entity.contacts.filter(c=>c.active).map(contact => (
                      <tr key={contact.id} style={{borderTop:'1px solid var(--border)'}}>
                        <td style={{padding:'8px 12px', fontWeight:500}}>{contact.name}</td>
                        <td style={{padding:'8px 12px', fontFamily:'var(--font-mono)', fontSize:12}}>{contact.email}</td>
                        <td style={{padding:'8px 12px', textAlign:'right'}}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setModal({ type: 'contact', entityId: entity.id, contact })}>Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* SUBSCRIBERS */}
          <div style={{marginTop:16}}>
            <div style={{fontSize:11, fontWeight:600, color:'var(--text-secondary)',
              padding:'7px 12px', background:'#f8f9fc',
              borderTop:'1px solid var(--border)'}}>
                SUBSCRIBERS
              </div>
              {(entity.subscribers || []).length === 0 ? (
                <p style={{fontSize:12, color:'var(--text-secondary)', padding:'8px 12px'}}>
                  No subscribers yet — add one above
                </p>
              ) : (
                <table style={{width:'100%', fontSize:13}}>
                  <thead>
                    <tr style={{background:'#f8f9fc'}}>
                      <th style={{padding:'7px 12px', textAlign:'left',
                        color:'var(--text-secondary)', fontSize:11, fontWeight:600}}>NAME</th>
                        <th style={{padding:'7px 12px', textAlign:'left',
                          color:'var(--text-secondary)', fontSize:11, fontWeight:600}}>EMAIL</th>
                          <th style={{padding:'7px 12px', textAlign:'left',
                            color:'var(--text-secondary)', fontSize:11, fontWeight:600}}>CADENCE</th>
                            <th style={{padding:'7px 12px', textAlign:'left',
                              color:'var(--text-secondary)', fontSize:11, fontWeight:600}}>STATUS</th>
                              <th style={{padding:'7px 12px'}}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {entity.subscribers.map(sub => (
                              <tr key={sub.id} style={{borderTop:'1px solid var(--border)'}}>
                                <td style={{padding:'8px 12px', fontWeight:500}}>{sub.name}</td>
                                <td style={{padding:'8px 12px', fontFamily:'var(--font-mono)',
                                  fontSize:12}}>{sub.email}</td>
                                  <td style={{padding:'8px 12px', fontSize:12, textTransform:'capitalize'}}>
                                    {sub.cadence}
                                  </td>
                                  <td style={{padding:'8px 12px'}}>
                                    {sub.confirmed
                                      ? <span className="badge badge-green">Confirmed</span>
                                      : <span className="badge badge-yellow">Pending</span>}
                                    {!sub.active && <span className="badge badge-gray" style={{marginLeft:4}}>Inactive</span>}
                                  </td>
                                  <td style={{padding:'8px 12px', textAlign:'right'}}>
                                    <button className="btn btn-ghost btn-sm"
                                      onClick={() => setModal({ type: 'subscriber', entityId: entity.id, subscriber: sub })}>
                                      Edit
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
        </div>
      ))}

      {modal?.type === 'entity' && (
        <Modal title={modal.entity ? 'Edit Customer' : 'Add Customer'} onClose={closeModal}>
          <EntityForm entity={modal.entity} onSave={closeModal} onClose={closeModal} />
        </Modal>
      )}
      {modal?.type === 'contact' && (
        <Modal title={modal.contact ? 'Edit Contact' : 'Add Contact'} onClose={closeModal}>
          <ContactForm entityId={modal.entityId} contact={modal.contact} onSave={closeModal} onClose={closeModal} />
        </Modal>
      )}
      {modal?.type === 'subscriber' && (
        <Modal title={modal.subscriber ? 'Edit Subscriber' : 'Add Subscriber'} onClose={closeModal}>
          <SubscriberForm
            entityId={modal.entityId}
            subscriber={modal.subscriber}
            onSave={closeModal}
          onClose={closeModal} />
        </Modal>
      )}
    </div>
  );
}
